import { DateSelectArg, EventClickArg } from '@fullcalendar/core';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import ltLocale from '@fullcalendar/core/locales/lt';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { Card } from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';

import { ICalendarEvent, ICalendarViewValue } from '@customTypes/calendar';
import { IGroup, IGroupSchedule } from '@customTypes/group';
import { IUser } from '@customTypes/user';
import { IconButtonAnimate } from 'components/animate';
import {
    createGroupScheduleEvent,
    deleteGroupScheduleEvent,
    getGroups,
    getGroupSchedule,
    getUserGroups,
    updateGroupScheduleEvent,
} from 'external/firebase/firestore/collections/groups';
import useLocales from 'hooks/useLocales';
import useResponsive from 'hooks/useResponsive';
import { fDate } from 'utils/formatTime';
import { hashString } from 'utils/hash';

import { CalendarToolbar, StyledCalendar } from './calendar';
import EventDialog, { CalendarCreateEditFormValues } from './calendar/EventDialog';

// ----------------------------------------------------------------------

async function generateScheduleEventsFromISchedule(group: IGroup): Promise<IGroupSchedule[]> {
    const { schedule, mode, address, city, instructions, meetingLinks, meetingRecordings, meetingInformation } = group;

    if (!schedule?.startDate || !schedule?.endDate) {
        return [];
    }

    const events: IGroupSchedule[] = [];
    const startDate = schedule.startDate.toDate();
    const endDate = schedule.endDate.toDate();

    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

    for (const dayName of daysOfWeek) {
        const daySchedule = schedule[dayName];
        if (daySchedule && daySchedule.startTime && daySchedule.endTime) {
            const dayScheduleForGenerator = {
                startTime: daySchedule.startTime.toDate(),
                endTime: daySchedule.endTime.toDate(),
                repeats: daySchedule.repeats,
            };

            const jsGetDayValue = daysOfWeek.indexOf(dayName) + 1;
            const adjustedDayOfWeek = jsGetDayValue === 7 ? 0 : jsGetDayValue;

            const currentDate = new Date(startDate);

            while (currentDate.getDay() !== adjustedDayOfWeek && currentDate <= endDate) {
                currentDate.setDate(currentDate.getDate() + 1);
            }

            while (currentDate <= endDate) {
                const startTime = new Date(currentDate);
                const startHours = dayScheduleForGenerator.startTime.getHours();
                const startMinutes = dayScheduleForGenerator.startTime.getMinutes();
                startTime.setHours(startHours, startMinutes, 0, 0);

                const endTime = new Date(currentDate);
                const endHours = dayScheduleForGenerator.endTime.getHours();
                const endMinutes = dayScheduleForGenerator.endTime.getMinutes();
                endTime.setHours(endHours, endMinutes, 0, 0);

                events.push({
                    id: fDate(startTime),
                    start: Timestamp.fromDate(startTime),
                    end: Timestamp.fromDate(endTime),
                    mode,
                    address,
                    city,
                    instructions,
                    meetingLinks: meetingLinks ? meetingLinks[dayName] : [],
                    meetingRecordings,
                    meetingInformation,
                });

                const daysToAdd =
                    dayScheduleForGenerator.repeats === 'weekly'
                        ? 7
                        : dayScheduleForGenerator.repeats === 'biweekly'
                        ? 14
                        : 999; // 'once' = very large number
                currentDate.setDate(currentDate.getDate() + daysToAdd);
            }
        }
    }

    return events;
}

// ----------------------------------------------------------------------

export async function createCalendarEventsFromSchedule(group: IGroup, showGroupId = true): Promise<ICalendarEvent[]> {
    const {
        id: groupId,
        name,
        mode,
        address,
        city,
        instructions,
        meetingLinks,
        meetingRecordings,
        meetingInformation,
    } = group;

    const groupSchedule = await generateScheduleEventsFromISchedule(group);

    return groupSchedule.reduce<ICalendarEvent[]>((acc, scheduleRecord) => {
        const eventStart = scheduleRecord.start?.toDate();
        const eventEnd = scheduleRecord.end?.toDate();

        if (!eventStart || !eventEnd) return acc;

        acc.push({
            id: hashString(groupId + name + eventStart.toISOString() + eventEnd.toISOString()),
            title: showGroupId ? `${groupId} ${name}` : name,
            color: 'red',
            groupId,
            mode: scheduleRecord.mode,
            address: `${address ?? ''}${city ? `, ${city}` : ''}`,
            city: scheduleRecord.city ?? '',
            instructions: scheduleRecord.instructions ?? '',
            meetingLinks: scheduleRecord.meetingLinks || [],
            meetingRecordings: scheduleRecord.meetingRecordings ?? '',
            meetingInformation: scheduleRecord.meetingInformation || meetingInformation || '',
            start: eventStart,
            end: eventEnd,
            generated: true,
        });

        return acc;
    }, []);
}

export async function createCalendarEvents(groups: IGroup[], showGroupId = true): Promise<ICalendarEvent[]> {
    const allEvents = await Promise.all(
        groups.map(async group => {
            const {
                id: groupId,
                name,
                mode,
                address,
                city,
                instructions,
                meetingLinks,
                meetingRecordings,
                meetingInformation,
            } = group;

            const groupSchedule = await getGroupSchedule(groupId);

            return groupSchedule.reduce<ICalendarEvent[]>((acc, scheduleRecord) => {
                const eventStart = scheduleRecord.start?.toDate();
                const eventEnd = scheduleRecord.end?.toDate();

                if (!eventStart || !eventEnd) return acc;

                acc.push({
                    id: hashString(groupId + name + eventStart.toISOString() + eventEnd.toISOString()),
                    title: showGroupId ? `${groupId} ${name}` : name,
                    color: 'red',
                    groupId,
                    mode: scheduleRecord.mode,
                    address: `${address ?? ''}${city ? `, ${city}` : ''}`,
                    city: scheduleRecord.city ?? '',
                    instructions: scheduleRecord.instructions ?? '',
                    meetingLinks: scheduleRecord.meetingLinks || [],
                    meetingRecordings: scheduleRecord.meetingRecordings ?? '',
                    meetingInformation: scheduleRecord.meetingInformation || meetingInformation || '',
                    start: eventStart,
                    end: eventEnd,
                    generated: true,
                });

                return acc;
            }, []);
        }),
    );

    return allEvents.flat();
}
// ----------------------------------------------------------------------

interface ScheduleCalendarProps {
    selectedUser?: IUser;
    showGroupId?: boolean;
    onRefreshRef?: (refreshFn: () => void) => void;
}

const ScheduleCalendar: React.FunctionComponent<ScheduleCalendarProps> = ({
    selectedUser,
    showGroupId = false,
    onRefreshRef,
}) => {
    const { translate, currentLang } = useLocales();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const isDesktop = useResponsive('up', 'sm');
    const calendarRef = useRef<FullCalendar>(null);

    const [events, setEvents] = useState<ICalendarEvent[]>([]);
    const [showEventDialog, setShowEventDialog] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null);
    const [selectedEventId, setSelectedEventId] = useState<string | null | undefined>(null);
    const [selectedRange, setSelectedRange] = useState<{
        start: Date;
        end: Date;
    } | null>(null);

    const [groups, setGroups] = useState<IGroup[]>([]);
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [date, setDate] = useState(new Date());
    const [view, setView] = useState<ICalendarViewValue>(isDesktop ? 'dayGridMonth' : 'listWeek');
    const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

    const fetchGroups = async () => {
        try {
            const groups = await getGroups();
            setGroups(groups);
            setError(undefined);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            // @ts-ignore
            setError(translate(err.message));
            setIsLoading(false);
        }
    };

    const fetchUserGroups = async (userId: string, role: IUser['role']) => {
        try {
            const userGroups = await getUserGroups(userId, role);
            setGroups(userGroups);
            setError(undefined);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            // @ts-ignore
            setError(translate(err.message));
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedEventId) {
            const event = events.find(event => event.id === selectedEventId);
            setSelectedEvent(event || null);
        } else {
            setSelectedEvent(null);
        }
    }, [selectedEventId]);

    const refreshCalendar = () => {
        setRefreshTimestamp(Date.now());
    };

    useEffect(() => {
        if (onRefreshRef) {
            onRefreshRef(refreshCalendar);
        }
    }, [onRefreshRef]);

    useEffect(() => {
        const handleCalendarRefresh = () => {
            refreshCalendar();
        };

        window.addEventListener('refreshCalendar', handleCalendarRefresh as EventListener);

        return () => {
            window.removeEventListener('refreshCalendar', handleCalendarRefresh as EventListener);
        };
    }, []);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const events = await createCalendarEvents(groups, showGroupId);
                setEvents(events);
            } catch (error) {
                console.error('Error fetching calendar events:', error);
            }
        };

        if (groups.length > 0) {
            fetchEvents();
        }
    }, [groups, showGroupId, refreshTimestamp]);

    useEffect(() => {
        if (selectedUser?.id) {
            fetchUserGroups(selectedUser.id, selectedUser.role);
        }
        if (selectedUser?.role === 'admin') {
            fetchGroups();
        }
    }, [selectedUser]);

    useEffect(() => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();

            const newView = isDesktop ? 'dayGridMonth' : 'listWeek';
            calendarApi.changeView(newView);
            setView(newView);
        }
    }, [isDesktop]);

    const formatCurrentTime = () => {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        return `${hours}:00:00`;
    };

    const handleOpenModal = () => {
        setShowEventDialog(true);
    };

    const handleCloseModal = () => {
        setShowEventDialog(false);
        setSelectedRange(null);
        setSelectedEventId(null);
    };

    const handleClickToday = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.today();
            setDate(calendarApi.getDate());
        }
    };

    const handleChangeView = (newView: ICalendarViewValue) => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.changeView(newView);
            setView(newView);
        }
    };

    const handleClickDatePrev = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.prev();
            setDate(calendarApi.getDate());
        }
    };

    const handleClickDateNext = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.next();
            setDate(calendarApi.getDate());
        }
    };

    const handleSelectEvent = (arg: EventClickArg) => {
        handleOpenModal();
        setSelectedEventId(arg.event.id);
    };

    const handleSelectRange = (arg: DateSelectArg) => {
        if (selectedUser?.role !== 'admin') {
            return;
        }
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.unselect();
        }
        setSelectedRange({
            start: arg.start,
            end: arg.end,
        });
        handleOpenModal();
    };

    const handleCreateUpdateEvent = async (eventData: CalendarCreateEditFormValues) => {
        try {
            const isUpdate = selectedEventId && selectedEvent?.start;
            await (isUpdate
                ? // @ts-ignore TS fails to narrow types of selectedEvent.start
                  updateGroupScheduleEvent(selectedEvent.start, eventData)
                : createGroupScheduleEvent(eventData));

            if (isUpdate) {
                setEvents(events =>
                    events.map(event => (event.id === selectedEventId ? { ...event, ...eventData } : event)),
                );
            } else {
                setEvents(events => [
                    ...events,
                    {
                        ...eventData,
                        id: hashString(eventData.groupId + eventData.start + eventData.end),
                        title: eventData.groupId,
                        color: 'red',
                    },
                ]);
            }

            enqueueSnackbar(translate(selectedEventId ? 'successfully_updated_event' : 'successfully_created_event'), {
                variant: 'success',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        } catch (e) {
            enqueueSnackbar(translate(selectedEventId ? 'failed_to_update_event' : 'failed_to_create_event'), {
                variant: 'success',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        }
    };

    const handleDeleteEvent = async () => {
        try {
            if (selectedEventId && selectedEvent?.groupId && selectedEvent?.start) {
                handleCloseModal();
                enqueueSnackbar(translate('successfully_deleted_event'), {
                    variant: 'success',
                    // eslint-disable-next-line react/display-name
                    action: key => (
                        <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                            <Icon icon={closeFill} />
                        </IconButtonAnimate>
                    ),
                });
                await deleteGroupScheduleEvent(selectedEvent?.groupId, fDate(selectedEvent?.start));
                setEvents(events => events.filter(event => event.id !== selectedEventId));
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <Card sx={{ overflow: 'visible' }}>
                <StyledCalendar>
                    <CalendarToolbar
                        date={date}
                        view={view}
                        onNextDate={handleClickDateNext}
                        onPrevDate={handleClickDatePrev}
                        onToday={handleClickToday}
                        onChangeView={handleChangeView}
                    />

                    {/* @ts-ignore */}
                    <FullCalendar
                        locale={currentLang.value === 'lt' ? ltLocale : enGbLocale}
                        weekends
                        selectable
                        nowIndicator
                        scrollTime={formatCurrentTime()}
                        rerenderDelay={10}
                        allDayMaintainDuration
                        ref={calendarRef}
                        initialDate={date}
                        initialView={view}
                        dayMaxEventRows={3}
                        eventDisplay='block'
                        events={events}
                        headerToolbar={false}
                        initialEvents={events}
                        select={handleSelectRange}
                        eventClick={handleSelectEvent}
                        height={isDesktop ? 720 : 'auto'}
                        contentHeight='auto'
                        expandRows
                        plugins={[listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
                    />
                </StyledCalendar>
            </Card>

            <EventDialog
                open={showEventDialog}
                event={selectedEvent}
                range={selectedRange}
                onClose={handleCloseModal}
                onCreateUpdateEvent={handleCreateUpdateEvent}
                onDeleteEvent={handleDeleteEvent}
                role={selectedUser?.role}
            />
        </>
    );
};

export default ScheduleCalendar;
